import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Camera, Loader2, Phone, Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_PHOTOS = 4;

const VEHICLES = [
  "Car / Sedan",
  "SUV / Pickup Truck",
  "Semi / Tractor-Trailer",
  "Box Truck / Fleet Vehicle",
  "RV / Camper / Motorhome",
  "Equipment / Machinery",
  "Junk Car / Scrap for Sale",
  "Demolition Job",
  "Other",
];

const initial = { name: "", phone: "", vehicle_type: VEHICLES[0], location: "", issue: "", urgency: "emergency" };

export default function TowRequestForm() {
  const [form, setForm] = useState(initial);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPhotos((p) => [...p, ...files].slice(0, MAX_PHOTOS));
    e.target.value = "";
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/tow-requests`, form);
      if (photos.length) {
        const fd = new FormData();
        photos.forEach((p) => fd.append("files", p));
        await axios.post(`${API}/tow-requests/${data.id}/photos`, fd);
      }
      setDone(data);
      toast.success("Request received — dispatch has your details.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send request. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-ink px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-200 focus:border-ember";

  return (
    <section id="request" data-testid="tow-request-section" className="relative bg-surface py-24 sm:py-32">
      <div className="grid-bg absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember" data-testid="request-overline">
              Request a Tow
            </p>
            <h2 className="mt-4 font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-6xl" data-testid="request-heading">
              Send It.<br />We're Rolling.
            </h2>
            <p className="mt-6 max-w-md text-base text-slate-400">
              Drop your details and our dispatcher gets pinged instantly. In a genuine emergency,
              calling is always fastest.
            </p>
            <a
              href="tel:+13302014612"
              data-testid="request-call-link"
              className="mt-8 inline-flex items-center gap-3 border border-ember/50 bg-ember/10 px-6 py-4 font-display text-xl font-bold uppercase tracking-wider text-ember transition-colors duration-200 hover:bg-ember hover:text-white"
            >
              <Phone className="h-5 w-5" />
              (330) 201-4612
            </a>
            <div className="mt-10 space-y-3 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
              <p data-testid="request-hours-note">Open 24 hours — every day of the year</p>
              <p data-testid="request-payment-note">Credit &amp; debit cards accepted</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            {done ? (
              <div data-testid="request-success-panel" className="flex h-full flex-col items-start justify-center border border-ember/40 bg-ink p-10">
                <CheckCircle2 className="h-12 w-12 text-ember" />
                <h3 className="mt-6 font-display text-3xl font-extrabold uppercase text-white">
                  Request Received
                </h3>
                <p className="mt-3 text-sm text-slate-400">
                  Reference <span className="font-mono text-ember" data-testid="request-reference-id">{done.id.slice(0, 8).toUpperCase()}</span> — dispatch has been notified.
                  We'll call <span className="text-white">{done.phone}</span> shortly.
                </p>
                <p className="mt-2 text-sm text-slate-400">
                  Need us right now? <a href="tel:+13302014612" className="text-ember underline" data-testid="success-call-link">Call (330) 201-4612</a>
                </p>
                <button
                  onClick={() => { setDone(null); setForm(initial); setPhotos([]); }}
                  data-testid="request-new-button"
                  className="mt-8 border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-widest text-white transition-colors duration-200 hover:border-ember hover:text-ember"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="tow-request-form" className="grid grid-cols-1 gap-5 border border-white/10 bg-ink/60 p-6 backdrop-blur-sm sm:grid-cols-2 sm:p-10">
                <div>
                  <label htmlFor="req-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Your Name</label>
                  <input id="req-name" data-testid="request-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="John Miller" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="req-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Callback Phone</label>
                  <input id="req-phone" data-testid="request-phone-input" required minLength={7} type="tel" value={form.phone} onChange={set("phone")} placeholder="(330) 555-0123" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="req-vehicle" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Vehicle Type</label>
                  <select id="req-vehicle" data-testid="request-vehicle-select" value={form.vehicle_type} onChange={set("vehicle_type")} className={inputCls}>
                    {VEHICLES.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="req-urgency" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Urgency</label>
                  <select id="req-urgency" data-testid="request-urgency-select" value={form.urgency} onChange={set("urgency")} className={inputCls}>
                    <option value="emergency">Emergency — stranded now</option>
                    <option value="standard">Standard — schedule a tow</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="req-location" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Vehicle Location</label>
                  <input id="req-location" data-testid="request-location-input" required minLength={3} value={form.location} onChange={set("location")} placeholder="I-76 near exit 12, or a street address" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="req-issue" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">What Happened?</label>
                  <textarea id="req-issue" data-testid="request-issue-input" required minLength={5} rows={4} value={form.issue} onChange={set("issue")} placeholder="Engine died, won't turn over. Loaded semi, need a heavy wrecker…" className={`${inputCls} resize-none`} />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                    Photos of the Vehicle / Damage <span className="text-slate-600">(optional, up to {MAX_PHOTOS})</span>
                  </label>
                  <input
                    id="req-photos"
                    data-testid="request-photo-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    multiple
                    onChange={addPhotos}
                    className="hidden"
                  />
                  <label
                    htmlFor="req-photos"
                    data-testid="request-photo-picker"
                    className="flex cursor-pointer items-center gap-3 border border-dashed border-white/20 bg-ink px-4 py-3.5 text-sm text-slate-400 transition-colors duration-200 hover:border-ember hover:text-ember"
                  >
                    <Camera className="h-4 w-4" />
                    {photos.length ? `Add more photos (${photos.length}/${MAX_PHOTOS} attached)` : "Attach photos — helps us send the right truck"}
                  </label>
                  {photos.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-3" data-testid="request-photo-previews">
                      {photos.map((p, i) => (
                        <div key={i} className="relative h-20 w-20 overflow-hidden border border-white/15">
                          <img src={URL.createObjectURL(p)} alt={p.name} className="h-full w-full object-cover" data-testid={`request-photo-preview-${i}`} />
                          <button
                            type="button"
                            onClick={() => removePhoto(i)}
                            data-testid={`request-photo-remove-${i}`}
                            className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center bg-ink/90 text-white hover:bg-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="request-tow-submit-button"
                    className="flex w-full items-center justify-center gap-3 bg-ember px-8 py-4 font-display text-lg font-bold uppercase tracking-wider text-white transition-all duration-200 hover:bg-emberdark hover:shadow-[0_0_36px_rgba(139,92,246,0.35)] disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                    {loading ? "Sending…" : "Send Tow Request"}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
